/**
 * magnet-preview.js — reusable live preview for a magnet, client-saas.
 *
 * Renders the installation's verified published SDK inside a sandboxed iframe.
 * Unsaved designs without an installation use the clearly labeled local candidate.
 * The preview uses the complete working
 * definition. Customers can exercise their own flow or inspect clearly labeled sample
 * states using the same appearance. Network actions are simulated. Used from the magnets card
 * (saved def), the look/mode configurator (working appearance), and the flow builder
 * (working steps) via `openPreviewModal(getPkg)`.
 */

import SDK_RAW from '../../../../shared/machfivemagnet.min.js?raw';
import { mountExperienceControls } from './magnet-experience.js';
import { installPreviewBridge, installFinishScene, listenToPreviewBridge, previewStructure } from './magnet-preview-bridge.js';
import { createQuestionScene, installQuestionScene } from './magnet-question-scene.js';
import { MAGNET_THEME_COLOR_FIELDS, magnetAppearancePreset, matchMagnetAppearancePreset } from '@sse/shared';
import { previewSubmissionResult, renderPreviewSubmissionResult } from './magnet-preview-result.js';

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

// Map a magnet source (bare fields from the API GET, or a working overlay) → the mag_*
// package the SDK consumes. Empty flow gets a friendly placeholder so preview never looks
// broken.
export function magnetToPackage(source = {}) {
  const s = source || {};
  if (s.definition && typeof s.definition === 'object') {
    const pkg = JSON.parse(JSON.stringify(s.definition));
    for (const [key, value] of Object.entries(s.appearance_patch || {})) {
      const target = key === 'mag_id' ? key : `mag_${key}`;
      if (['inline_width', 'inline_height', 'success_cta', 'theme_vars'].includes(key) &&
          (!value || (typeof value === 'object' && !Object.keys(value).length))) delete pkg[target];
      else if (key === 'ab') {
        pkg.mag_ab = { ...(pkg.mag_ab || {}), ...value,
          b: Object.fromEntries(Object.entries(value.b || {}).map(([k, v]) => [k.startsWith('mag_') ? k : `mag_${k}`, v])) };
      } else pkg[target] = value;
    }
    if (Array.isArray(s.steps)) pkg.mag_macro_steps = s.steps;
    if (s.routes && typeof s.routes === 'object') pkg.mag_routes = s.routes;
    if (Object.hasOwn(s, 'marketing_excluded')) pkg.mag_marketing_excluded = s.marketing_excluded === true;
    pkg.mag_footer_branding = s.footer_branding || null;
    return pkg;
  }
  const steps = Array.isArray(s.steps) && s.steps.length
    ? s.steps
    : [{ id: '1', step_type: 'message', step_prompts: ['👋 Add steps in the flow builder to preview the conversation.'], step_options: [], step_columns: [], step_description: '' }];
  return {
    ...(s.agent_tools != null ? { mag_agent_tools: s.agent_tools } : {}),
    mag_footer_branding: s.footer_branding || null,
    mag_marketing_excluded: s.marketing_excluded === true,
    mag_display_mode: s.display_mode || 'chat',
    mag_trigger: 'on_load',
    mag_theme_color: /^#[0-9a-fA-F]{6}$/.test(s.theme_color || '') ? s.theme_color : '#0072BA',
    mag_theme_mode: s.theme_mode || 'auto',
    mag_title: (s.title && String(s.title).trim()) || s.name || 'How can we help?',
    mag_subtitle: s.subtitle != null ? s.subtitle : 'A few details so we can follow up.',
    mag_launcher_label: s.launcher_label || '',
    mag_visual_style: s.visual_style || '', mag_motion_style: s.motion_style || '', mag_message_style: s.message_style || '',
    mag_presentation_style: s.presentation_style || 'conversation',
    mag_launcher_style: s.launcher_style || 'label',
    mag_launcher_hint: s.launcher_hint || '',
    mag_visitor_controls: s.visitor_controls === true,
    mag_home_title: s.home_title || '',
    mag_legal_notice: s.legal_notice,
    mag_consent: s.consent,
    mag_logo_url: s.logo_url || '',
    mag_logo_fit: s.logo_fit === 'cover' ? 'cover' : 'contain',
    mag_logo_scale: s.logo_scale ?? 100,
    mag_summary: !!s.summary,
    mag_summary_heading: s.summary_heading || '',
    mag_summary_confirm_label: s.summary_confirm_label || '',
    mag_home: !!s.home,
    mag_welcome_experience: s.welcome_experience,
    mag_welcome: s.welcome != null ? s.welcome : '',
    mag_actions: Array.isArray(s.actions) ? s.actions : [],
    mag_macro_steps: steps,
    mag_routes: s.routes && typeof s.routes === 'object' ? s.routes : {},
    mag_ab: s.ab || undefined,
    // Brand theming + inline sizing + post-submit (mirror of the builder's new fields).
    mag_font: s.font || '',
    mag_theme_vars: (s.theme_vars && typeof s.theme_vars === 'object' && Object.keys(s.theme_vars).length) ? s.theme_vars : undefined,
    mag_inline_width: s.inline_width || '',
    mag_inline_height: s.inline_height || '',
    mag_input_placeholder: s.input_placeholder || '',
    mag_success_title: s.success_title || '',
    mag_success_text: s.success_text != null ? s.success_text : undefined,
    mag_success_cta: (s.success_cta && s.success_cta.url && s.success_cta.label) ? s.success_cta : undefined,
    // hide_launcher is intentionally NOT forced in preview — the preview auto-opens
    // (mag_trigger 'on_load') so the panel is always visible to inspect.
  };
}

const SAMPLE_STATES = [
  ['welcome', 'Welcome'], ['choices', 'Choices'], ['text', 'Text entry'],
  ['review', 'Review'], ['sending', 'Sending'], ['error', 'Error'], ['success', 'Success'],
];

// Sample states borrow the working appearance, never the customer's flow or answers.
// The runtime below reaches each state through the public controls in the real SDK.
function previewPackage(pkg, state, theme) {
  const preview = { ...pkg, mag_trigger: 'on_load', mag_hide_launcher: false };
  if (theme === 'light' || theme === 'dark') {
    preview.mag_theme_mode = theme;
    const preset = matchMagnetAppearancePreset(pkg.mag_font, pkg.mag_theme_vars);
    // Preset surfaces override the SDK's base theme. Select the same style's
    // alternate palette for this preview without mutating the working package.
    if (preset) preview.mag_theme_vars = magnetAppearancePreset(preset, theme).theme_vars;
  }
  if (!SAMPLE_STATES.some(([value]) => value === state)) return preview;
  preview.mag_home = state === 'welcome';
  preview.mag_summary = true;
  if (preview.mag_home && !preview.mag_actions?.length) {
    preview.mag_actions = [{ type: 'chat', label: 'Get started' }, { type: 'support', label: 'Ask a question' }];
  }
  const choice = {
    id: 'preview-choice', step_type: 'single_select', step_columns: ['project_type'],
    step_prompts: ['What can we help you with?'],
    step_options: [
      { label: 'Start a new project', icon: 'description', description: 'Tell us what you have in mind' },
      { label: 'Improve something existing', icon: 'build', description: 'Find the right next step' },
      { label: 'Explore my options', icon: 'help', description: 'Get a little guidance first' },
    ],
  };
  const email = {
    id: 'preview-email', step_type: 'email', step_columns: ['email'], step_options: [],
    step_prompts: ['Where should we send your next steps?'], step_config: { placeholder: 'you@example.com' },
  };
  preview.mag_macro_steps = state === 'text' ? [email] : [choice, email];
  return preview;
}

// Serialized into the sandbox BEFORE the SDK captures fetch. There is no network
// passthrough: real-flow previews must be as safe as the canned sample states.
function installPreviewNetwork(state, preserveEditorFocus, navigation = null) {
  // The SDK's automatic open must not pull focus/scroll from the authoring form.
  // Once the user enters this sandbox, normal visitor keyboard behavior resumes.
  if (preserveEditorFocus) {
    const focus = HTMLElement.prototype.focus;
    HTMLElement.prototype.focus = function (options) {
      if (document.hasFocus()) focus.call(this, options);
    };
  }
  let captures = 0;
  let runtimeRoute = 'chat';
  let interacted = false;
  for (const type of ['pointerdown', 'keydown']) document.addEventListener(type, event => { if (event.isTrusted) interacted = true; }, true);
  const reportNavigation = body => {
    if (!navigation) return;
    try {
      const event = JSON.parse(body);
      if (event.event === 'route') {
        runtimeRoute = event.detail;
        if (['call', 'email'].includes(runtimeRoute)) window.parent.postMessage({ type: 'm5m-studio-preview', token: navigation.token, kind: 'position', position: { route: runtimeRoute, index: null, interactive: interacted } }, '*');
      }
      if (event.event !== 'step' || !['chat', 'book', 'support'].includes(runtimeRoute)) return;
      const match = /^(\d+)\/(\d+)$/.exec(event.detail || '');
      if (!match) return;
      const position = { route: runtimeRoute, index: Number(match[1]) - 1, total: Number(match[2]), interactive: interacted };
      window.parent.postMessage({ type: 'm5m-studio-preview', token: navigation.token, kind: 'position', position }, '*');
    } catch { /* Unrecognized SDK telemetry must not interrupt a simulated visit. */ }
  };
  const response = (data, ok = true) => Promise.resolve({ ok, status: ok ? 200 : 503, json: () => Promise.resolve(data) });
  window.fetch = function (url, options) {
    const path = String(url);
    if (/\/event(?:\?|$)/.test(path)) reportNavigation(options?.body);
    if (/\/capture(?:\?|$)/.test(path)) {
      captures++;
      if (state === 'sending') return new Promise(() => {});
      if (state === 'error' && captures === 1) return response({ ok: false }, false);
      if (navigation?.captureResults) {
        try {
          const body = JSON.parse(options?.body);
          window.parent.postMessage({ type: 'm5m-studio-preview', token: navigation.token, kind: 'capture-result', capture: {
            fields: body.fields, selections: body.selections, scheduler_timezone: body.scheduler_tz,
            route: body.answer_contract?.route || runtimeRoute,
          } }, '*');
        } catch { /* An invalid test request must not fabricate a result. */ }
      }
      return response({ ok: true });
    }
    if (/\/booking\/slots(?:\?|$)/.test(path)) {
      const from = new URL(path, 'https://preview.invalid').searchParams.get('from');
      const day = from ? new Date(from) : new Date();
      day.setDate(day.getDate() + 1); day.setHours(10, 0, 0, 0);
      return response({ ok: true, duration_mins: 30, horizon_days: 90, service_name: 'Sample appointment',
        slots: [0, 1, 2].map((offset) => ({ start: new Date(day.getTime() + offset * 3600000).toISOString(), staff_id: 'preview' })) });
    }
    if (/\/(?:event|booking)(?:\?|$)/.test(path)) return response({ ok: true });
    return response({ ok: false }, false);
  };
  try { navigator.sendBeacon = () => true; } catch { /* the iframe CSP also blocks requests */ }
  const showDestination = (url) => window.parent.postMessage({ type: 'm5m-preview-link', url: String(url) }, '*');
  window.open = (url) => { showDestination(url); return null; };
  document.addEventListener('click', (event) => {
    const link = event.composedPath().find((node) => node.tagName === 'A');
    if (link) { event.preventDefault(); showDestination(link.href); }
  }, true);
}

// No SDK testing API: exercise the same controls that a visitor uses. The driver
// disconnects at its destination so Review edits and Error retry remain interactive.
function showPreviewSample(state, route, studio = false) {
  let attempts = 0;
  const start = () => {
    const host = document.querySelector('[id^="m5m-magnet-"]');
    const shadow = host?.shadowRoot;
    if (!shadow || (studio && !window.MachFiveMagnet?.instances?.length)) {
      if (studio && ++attempts < 200) setTimeout(start, 40);
      return;
    }
    // Authoring always starts with the real panel visible. The public API bypasses
    // on-load delays/session suppression without changing its display mode.
    if (studio) window.MachFiveMagnet.open();
    if (state === 'flow' && route && route !== 'entry') {
      if (!studio) shadow.querySelector('.m5m-launcher')?.click();
      shadow.querySelector(`[data-m5m-act="${route}"]`)?.click();
    }
    // External providers cannot load inside a preview (CSP), so replace their blank
    // iframe with an explanation and expose the destination in the preview controls.
    const showBookingDestinations = () => {
      for (const frame of shadow.querySelectorAll('iframe')) {
        const notice = document.createElement('div');
        notice.className = 'm5m-cal-done';
        notice.setAttribute('role', 'status');
        notice.textContent = 'Your booking provider opens here on your published site. See its destination above this preview.';
        frame.replaceWith(notice);
        window.parent.postMessage({ type: 'm5m-preview-link', url: frame.src, booking: true }, '*');
      }
    };
    const bookingObserver = new MutationObserver(showBookingDestinations);
    bookingObserver.observe(shadow, { childList: true, subtree: true });
    showBookingDestinations();
    if (!['review', 'sending', 'error', 'success'].includes(state)) return;
    let choiceSent = false;
    let emailSent = false;
    let confirmed = false;
    let timeout;
    const stop = () => { observer.disconnect(); clearTimeout(timeout); };
    const advance = () => {
      const choice = shadow.querySelector('.m5m-choice');
      if (!choiceSent && choice) { choiceSent = true; choice.click(); return; }
      const input = shadow.querySelector('.m5m-form input');
      if (!emailSent && input) {
        emailSent = true;
        input.value = 'alex@example.test';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.closest('form').requestSubmit();
        return;
      }
      const confirm = shadow.querySelector('.m5m-sum-confirm');
      if (confirm && !confirmed) {
        if (state === 'review') { stop(); return; }
        confirmed = true;
        confirm.click();
        stop();
      }
    };
    const observer = new MutationObserver(advance);
    observer.observe(shadow, { childList: true, subtree: true });
    timeout = setTimeout(stop, 10000);
    advance();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}

// Exported so focused tests can execute this exact iframe document and the real SDK.
export function buildPreviewDocument(pkg, { state = 'flow', theme = 'saved', route = 'entry', variant = 'A', sdk = SDK_RAW, preserveEditorFocus = false, studio = false, bridge = null, questionScene = null, finishScene = null } = {}) {
  const scene = questionScene ? createQuestionScene(pkg, questionScene.selection, variant) : null;
  if (scene) {
    pkg = scene.pkg || pkg;
    studio = true; state = 'flow'; route = 'entry'; variant = 'A'; bridge = null;
  }
  if (studio) state = 'flow';
  if (finishScene && ['review', 'success'].includes(finishScene.state)) {
    state = finishScene.state; route = 'entry'; bridge = null;
  }
  if (!SAMPLE_STATES.some(([value]) => value === state)) state = 'flow';
  if (!['entry', 'chat', 'book', 'support'].includes(route)) route = 'entry';
  // Inspect a deliberate arm; preview must never randomize or persist an assignment.
  const selected = variant === 'B' && pkg.mag_ab?.b ? { ...pkg, ...pkg.mag_ab.b } : pkg;
  const preview = previewPackage({ ...selected, mag_ab: null }, state, theme);
  // A plain conversation already starts at chat. Do not add a welcome/action
  // bar merely because the workspace follows that selected path.
  if (route === 'chat' && !preview.mag_home) route = 'entry';
  if (state === 'flow' && route !== 'entry') {
    preview.mag_home = true;
    preview.mag_actions = [...(preview.mag_actions || [])];
    if (!preview.mag_actions.some(a => a.type === route)) preview.mag_actions.push({ type: route });
  }
  const defs = JSON.stringify([preview]);
  const hydrated = (scene?.reason ? '' : sdk)
    .replace(/##APPGUID##/g, () => 'preview')
    .replace(/["']##MAGNET_DEFINITIONS##["']/g, () => defs)
    .replace(/<\/script>/gi, '<\\/script>'); // last: also escapes any </script> in injected copy
  const inline = preview.mag_display_mode === 'inline';
  const context = preview.mag_theme_mode === 'dark' ? 'dark' : preview.mag_theme_mode === 'auto' ? 'auto' : 'light';
  return `<!DOCTYPE html><html lang="en" data-theme="${context}"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="connect-src 'none'; frame-src 'none'; form-action 'none'">
<style>
  :root { color-scheme: light; --pv-bg: #eef2f7; --pv-surface: #fff; --pv-border: #e2e8f0; --pv-skeleton: #dbe3ee; --pv-text: #334155; }
  :root[data-theme="dark"] { color-scheme: dark; --pv-bg: #141b26; --pv-surface: #202b3a; --pv-border: #344254; --pv-skeleton: #344254; --pv-text: #e2e8f0; }
  @media (prefers-color-scheme: dark) { :root[data-theme="auto"] { color-scheme: dark; --pv-bg: #141b26; --pv-surface: #202b3a; --pv-border: #344254; --pv-skeleton: #344254; --pv-text: #e2e8f0; } }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: var(--pv-bg); color: #334155; }
  .pv-nav { display: flex; align-items: center; gap: 14px; padding: 14px 22px; background: var(--pv-surface); border-bottom: 1px solid var(--pv-border); }
  .pv-logo { width: 26px; height: 26px; border-radius: 7px; background: linear-gradient(135deg,#c7d2e0,#aab8cc); }
  .pv-navlinks { display: flex; gap: 14px; margin-left: auto; }
  .pv-navlinks i { display: block; width: 46px; height: 9px; border-radius: 5px; background: var(--pv-border); }
  .pv-hero { padding: 40px 22px 20px; text-align: center; }
  .pv-hero .l1 { height: 20px; width: min(300px, 70%); margin: 0 auto 12px; border-radius: 7px; background: var(--pv-skeleton); }
  .pv-hero .l2 { height: 12px; width: min(420px, 84%); margin: 0 auto; border-radius: 6px; background: var(--pv-skeleton); }
  .pv-content { padding: 26px 22px 90px; }
  .pv-slot { max-width: 960px; margin: 0 auto; }
  .pv-cards { max-width: 680px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .pv-card { height: 120px; border-radius: 12px; background: var(--pv-surface); border: 1px solid var(--pv-border); }
  .pv-scene-unavailable { max-width: 440px; margin: 32px auto; padding: 24px; border: 1px solid var(--pv-border); border-radius: 16px; background: var(--pv-surface); color: var(--pv-text, #334155); line-height: 1.5; }
  ${studio ? 'html, body { background: var(--pv-bg); min-height: 100%; } .pv-content { padding: 8px; } .pv-slot { max-width: 960px; }' : ''}
  @media (max-width: 460px) { .pv-cards { grid-template-columns: 1fr; } .pv-navlinks { display: none; } }
</style></head>
<body>
  ${studio ? '' : '<div class="pv-nav"><span class="pv-logo"></span><span class="pv-navlinks"><i></i><i></i><i></i></span></div><div class="pv-hero"><div class="l1"></div><div class="l2"></div></div>'}
  <div class="pv-content">
    ${scene?.reason ? `<section class="pv-scene-unavailable" role="status"><strong>Question preview</strong><p>${esc(scene.reason)}</p></section>` : inline ? '<div class="pv-slot"><div data-m5m-inline></div></div>' : studio ? '' : '<div class="pv-cards"><div class="pv-card"></div><div class="pv-card"></div></div>'}
  </div>
  <script>(${installPreviewNetwork.toString()})(${JSON.stringify(state)}, ${JSON.stringify(preserveEditorFocus)}, ${JSON.stringify(studio && bridge ? { token: bridge.token, captureResults: !!bridge.captureResults } : null)});<\/script>
  <script>${hydrated}<\/script>
  ${scene?.reason ? '' : `<script>(${showPreviewSample.toString()})(${JSON.stringify(state)}, ${JSON.stringify(route)}, ${JSON.stringify(studio)});<\/script>`}
  ${scene ? `<script>(${installQuestionScene.toString()})(${JSON.stringify({ token: questionScene.token, unavailable: !!scene.reason, selectedStepId: questionScene.selection?.step?.id }).replace(/</g, '\\u003c')});<\/script>` : ''}
  ${studio && bridge ? `<script>(${installPreviewBridge.toString()})(${JSON.stringify(bridge).replace(/</g, '\\u003c')});<\/script>` : ''}
  ${finishScene ? `<script>(${installFinishScene.toString()})(${JSON.stringify(finishScene).replace(/</g, '\\u003c')});<\/script>` : ''}
</body></html>`;
}

let stylesInjected = false;
function ensureStyles() {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'm5t-preview-styles';
  style.textContent = `
    .pv-submission-result { flex: 0 0 auto; margin: 0 10px 8px; border: 1px solid var(--color-border, #D4DCE8); border-radius: 10px; background: var(--color-bg, #fff); color: var(--color-text, #0B1220); font-size: .85rem; }
    .pv-submission-result[hidden] { display: none; }
    .pv-submission-result > summary { cursor: pointer; padding: 10px 12px; font-weight: 600; }
    .pv-submission-result > div { padding: 0 12px 12px; max-height: 38vh; overflow: auto; }
    .pv-submission-result p { color: var(--color-text-muted, #4A5A78); line-height: 1.45; }
    .pv-submission-result h4 { margin: 14px 0 6px; }
    .pv-submission-result dl { margin: 0; }
    .pv-submission-result dt { font-weight: 600; margin-top: 8px; overflow-wrap: anywhere; }
    .pv-submission-result dd { margin: 3px 0 10px; white-space: pre-wrap; overflow-wrap: anywhere; }
    .pv-submission-result ul { margin: 0; padding-left: 20px; }
    .pv-scrim { position: fixed; inset: 0; background: rgba(11,18,32,0.55); z-index: 3000; opacity: 0; transition: opacity 0.18s ease; display: flex; align-items: center; justify-content: center; padding: 20px; }
    .pv-scrim.open { opacity: 1; }
    .pv-modal { width: min(880px, 96vw); max-height: 94vh; display: flex; flex-direction: column; background: var(--color-bg, #fff); color: var(--color-text, #0B1220); border-radius: var(--radius-lg, 16px); box-shadow: 0 24px 70px rgba(11,18,32,0.4); opacity: 0; transform: translateY(8px); transition: opacity 0.18s ease, transform 0.18s ease; }
    .pv-scrim.open .pv-modal { opacity: 1; transform: none; }
    .pv-head { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 14px 18px; border-bottom: 1px solid var(--color-border, #D4DCE8); }
    .pv-title { font-weight: 700; font-size: 1rem; display: flex; align-items: center; gap: 8px; }
    .pv-title .material-symbols-outlined { font-size: 20px; color: var(--color-primary-600, #0072BA); }
    .pv-ctrls { margin-left: auto; display: flex; flex-wrap: wrap; align-items: center; gap: 8px; max-width: 100%; }
    .pv-seg { display: inline-flex; border: 1px solid var(--color-border, #D4DCE8); border-radius: 9px; overflow: hidden; }
    .pv-seg button { appearance: none; border: 0; background: transparent; cursor: pointer; font: inherit; font-size: 0.8rem; font-weight: 600; padding: 6px 12px; color: var(--color-text-muted, #4A5A78); display: inline-flex; align-items: center; gap: 5px; }
    .pv-seg button + button { border-left: 1px solid var(--color-border, #D4DCE8); }
    .pv-seg button.is-on { background: var(--color-primary-600, #0072BA); color: #fff; }
    .pv-seg button .material-symbols-outlined { font-size: 17px; }
    .pv-iconbtn { appearance: none; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); cursor: pointer; width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; color: var(--color-text-muted, #4A5A78); }
    .pv-iconbtn:hover { border-color: var(--color-primary-600, #0072BA); color: var(--color-primary-600, #0072BA); }
    .pv-iconbtn .material-symbols-outlined { font-size: 19px; }
    .pv-body { padding: 20px; min-height: 0; overflow: auto; background: var(--color-surface, #F4F7FB); display: flex; justify-content: center; border-radius: 0 0 var(--radius-lg, 16px) var(--radius-lg, 16px); }
    .pv-body > div { width: 100%; }
    .pv-stage { transition: width 0.2s ease; margin: 0 auto; }
    .pv-stage[data-device="desktop"] { width: 100%; max-width: 820px; }
    .pv-stage[data-device="tablet"] { width: 640px; max-width: 100%; }
    .pv-stage[data-device="mobile"] { width: 390px; }
    .pv-frame { width: 100%; border: 0; background: #eef2f7; border-radius: 12px; box-shadow: 0 6px 24px rgba(11,18,32,0.12); }
    .pv-stage[data-device="desktop"] .pv-frame { height: 560px; }
    .pv-stage[data-device="tablet"] .pv-frame { height: 600px; }
    .pv-stage[data-device="mobile"] .pv-frame { height: 620px; border-radius: 22px; border: 8px solid #1e293b; box-shadow: 0 10px 30px rgba(11,18,32,0.28); }
    .pv-scrim--full { padding: 12px; }
    .pv-scrim--full .pv-modal { width: 100%; height: 100%; max-height: none; }
    /* Inspector rows use a full-width flex basis inside the toolbar. Once moved
       into the vertical modal, they must size to content, not the modal height. */
    .pv-scrim--full .pv-modal > :not(.pv-body) { flex: 0 0 auto; }
    .pv-scrim--full .pv-modal > .pv-options { max-height: 42vh; overflow: auto; }
    .pv-scrim--full .pv-body { flex: 1; }
    .pv-scrim--full .pv-body > div { display: flex; flex-direction: column; min-height: 0; }
    .pv-scrim--full .pv-stage { display: flex; flex: 1; min-height: 240px; max-width: 100%; }
    .pv-scrim--full .pv-stage[data-device="desktop"] { width: 100%; }
    .pv-scrim--full .pv-stage .pv-frame { flex: 1; height: 100%; min-height: 0; display: block; box-sizing: border-box; }
    .pv-scrim--full .pv-hintwrap { flex-shrink: 0; }
    @media (max-width: 600px) {
      .pv-scrim--full { padding: 0; }
      .pv-scrim--full .pv-modal { border-radius: 0; }
      .pv-scrim--full .pv-body { padding: 10px; }
      .pv-scrim--full .pv-head { position: relative; gap: 8px; padding: 10px; }
      .pv-scrim--full .pv-title { padding-right: 42px; }
      .pv-scrim--full [data-x="close"] { position: absolute; top: 10px; right: 10px; }
      .pv-scrim--full .pv-ctrls { margin-left: 0; }
    }
    .pv-hint { text-align: center; font-size: 0.74rem; color: var(--color-text-subtle, #6C7C9A); margin: 10px 0 0; }
    .pv-inspector { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 16px; padding: 12px 18px; border-bottom: 1px solid var(--color-border, #D4DCE8); }
    .pv-inspector label { display: flex; align-items: center; gap: 7px; font-size: 0.76rem; font-weight: 600; color: var(--color-text-muted, #4A5A78); }
    .pv-inspector select { max-width: 100%; padding: 7px 28px 7px 9px; border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-sm, 8px); background: var(--color-bg, #fff); color: var(--color-text, #0B1220); font: inherit; }
    .pv-inspector select:focus-visible { outline: 2px solid var(--color-primary-600, #0072BA); outline-offset: 2px; }
    .pv-inspector__note { flex-basis: 100%; margin: 0; font-size: 0.72rem; line-height: 1.4; color: var(--color-text-subtle, #6C7C9A); }
    .pv-link-notice { display: flex; align-items: flex-start; gap: 10px; flex-basis: 100%; padding: 9px 10px; border-radius: var(--radius-sm, 8px); background: var(--color-surface, #F4F7FB); color: var(--color-text-muted, #4A5A78); font-size: 0.74rem; line-height: 1.4; }
    .pv-link-notice[hidden] { display: none; }
    .pv-link-notice span { min-width: 0; overflow-wrap: anywhere; }
    .pv-link-notice button { margin-left: auto; border: 0; background: transparent; color: inherit; cursor: pointer; font: inherit; text-decoration: underline; }
    .pv-scrim--full > .pv-modal > .pv-options { padding: 10px 18px; }
    .pv-scrim--full > .pv-modal > .pv-inspector__note, .pv-scrim--full > .pv-modal > .pv-runtime-note { margin: 0; padding: 0 18px 10px; }
    .pv-options > summary { cursor: pointer; font-size: 0.8rem; color: var(--color-text-muted, #4A5A78); }
    .pv-pane .pv-inspector { padding: 0 0 12px; margin-bottom: 12px; gap: 8px 12px; }
    .pv-inspector { max-height: 42vh; overflow: auto; flex-shrink: 0; }
    .pv-experience { flex-basis: 100%; display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; }
    .pv-experience p { flex-basis: 100%; margin: 0; font-size: 0.75rem; line-height: 1.5; }
    .pv-experience [hidden] { display: none; }
    .pv-experience [data-pv-upgrade] { padding: 12px; border: 1px solid var(--color-border, #D4DCE8); border-radius: 8px; display: grid; gap: 10px; }
    .pv-experience [data-pv-upgrade][hidden] { display: none; }
    .pv-experience button { border: 1px solid var(--color-border, #D4DCE8); border-radius: 8px; padding: 8px 12px; background: var(--color-bg, #fff); color: var(--color-text, #0B1220); font: inherit; cursor: pointer; }
    .pv-experience [data-pv-apply] { background: var(--color-primary-600, #0072BA); color: #fff; justify-self: start; }
    .pv-experience button:disabled { opacity: 0.55; cursor: default; }
    .pv-experience summary { cursor: pointer; font-size: 0.78rem; font-weight: 600; }
    .pv-experience details[open] p { margin-top: 8px; }
    .pv-frame[hidden], .pv-pane__frame[hidden] { display: none; }
    /* embedded live pane (edit modal's right column) */
    .pv-pane { display: flex; flex-direction: column; height: 100%; }
    .pv-pane__hd { display: flex; align-items: center; gap: 8px; font-size: 0.82rem; font-weight: 700; color: var(--color-text-muted, #4A5A78); padding: 0 0 10px; }
    .pv-pane__hd > .material-symbols-outlined { font-size: 18px; color: var(--color-primary-600, #0072BA); }
    .pv-pane__hdright { margin-left: auto; display: flex; align-items: center; gap: 8px; }
    .pv-pane__seg button { padding: 5px 8px; }
    .pv-pane__seg button .material-symbols-outlined { font-size: 17px; }
    .pv-pane__badge { display: inline-flex; align-items: center; gap: 5px; font-size: 0.68rem; font-weight: 600; color: var(--color-accent, #0072BA); background: var(--color-accent-soft, rgba(0,114,186,0.12)); padding: 2px 9px; border-radius: 999px; }
    .pv-pane__badge[hidden] { display: none; }
    .pv-pane__dot { width: 6px; height: 6px; border-radius: 50%; background: var(--color-primary-600, #0072BA); animation: pvPulse 0.8s ease-in-out infinite; }
    @keyframes pvPulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
    .pv-pane__body { flex: 1 1 auto; min-height: 0; }
    .pv-pane__vp { width: 100%; height: 100%; overflow: auto; }
    /* 1:1, no downscaling — the iframe width IS the viewport that drives the widget's
       responsive layout (mobile full-screen panel vs desktop floating card). */
    .pv-pane__frame { height: 100%; border: 0; border-radius: 12px; background: #eef2f7; box-shadow: 0 6px 24px rgba(11,18,32,0.10); display: block; margin: 0 auto; }
    .pv-pane__frame[data-device="mobile"] { width: 390px; }
    .pv-pane__frame[data-device="tablet"] { width: 640px; }
    .pv-pane__frame[data-device="desktop"] { width: 820px; }
    .pv-pane--studio { min-height: 0; }
    .pv-pane--studio .pv-pane__body { min-height: 0; }
    .pv-pane--studio .pv-pane__hd { flex-wrap: wrap; }
    .pv-pane--studio .pv-pane__frame { box-shadow: none; background: transparent; border-radius: 0; }
    .pv-pane--studio .pv-pane__frame[data-device="mobile"] { width: min(390px, 100%); }
    .pv-pane--studio .pv-pane__frame[data-device="desktop"] { width: 100%; min-width: 640px; }
    .pv-studio-status { margin: 0 0 8px; font-size: 0.75rem; color: var(--color-text-muted, #4A5A78); }
    .pv-studio-status:empty { display: none; }
    .pv-question-tools { display: inline-flex; align-items: center; gap: 5px; }
    .pv-question-tools[hidden] { display: none; }
    .pv-question-tools .pv-seg button { padding: 5px 8px; font-size: 0.72rem; }
    .pv-pane .pv-hint { margin-top: 8px; }
    /* clickable "why might it look different" info icon + popover */
    .pv-hintwrap { position: relative; }
    .pv-info { appearance: none; border: 0; background: transparent; cursor: pointer; color: var(--color-text-subtle, #6C7C9A); padding: 0 2px; vertical-align: -3px; line-height: 1; }
    .pv-info:hover { color: var(--color-primary-600, #0072BA); }
    .pv-info .material-symbols-outlined { font-size: 15px; }
    .pv-info-pop { position: absolute; left: 0; right: 0; bottom: 100%; margin-bottom: 8px; background: var(--color-bg, #fff); border: 1px solid var(--color-border, #D4DCE8); border-radius: 10px; box-shadow: 0 8px 26px rgba(11,18,32,0.16); padding: 12px 14px; font-size: 0.78rem; line-height: 1.5; color: var(--color-text, #0B1220); text-align: left; z-index: 5; }
    .pv-info-pop strong { display: block; margin-bottom: 4px; }
    .pv-info-pop[hidden] { display: none; }
    .pv-hint--style { margin-top: 3px; }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
}

// Two hint lines: (1) the capture disclaimer, and (2) a separate custom-styling note with a
// clickable info icon that toggles a plain-language popover. Shared by the pane and the modal.
function infoHintHTML(captureText) {
  return `<div class="pv-hintwrap">
    <p class="pv-hint">${captureText}</p>
    <p class="pv-hint pv-hint--style">Custom styling on your site won't be shown here
      <button type="button" class="pv-info" data-pv-info aria-label="More about custom styling"><span class="material-symbols-outlined" aria-hidden="true">info</span></button>
    </p>
    <div class="pv-info-pop" data-pv-info-pop hidden>
      <strong>About custom styling</strong>
      This preview shows the magnet exactly as you set it up here. If you style over it with your own CSS on your website or app, those changes won't show in this preview, so the live version can look a little different.
    </div>
  </div>`;
}
function wireInfoHint(root) {
  const btn = root.querySelector('[data-pv-info]');
  const pop = root.querySelector('[data-pv-info-pop]');
  if (btn && pop) btn.addEventListener('click', () => { pop.hidden = !pop.hidden; });
}

function inspectionControlsHTML() {
  return `<div class="pv-inspector">
    <label>View <select data-pv-state>
      <option value="flow">Your flow</option>
      <optgroup label="Sample states">${SAMPLE_STATES.map(([value, label]) => `<option value="${value}">${label}</option>`).join('')}</optgroup>
    </select></label>
    <label>Route <select data-pv-route>
      <option value="entry">Start as configured</option><option value="chat">Chat</option><option value="book">Book</option><option value="support">Support</option>
    </select></label>
    <label>Variant <select data-pv-variant><option value="A">A</option><option value="B">B</option></select></label>
    <label>Theme <select data-pv-theme>
      <option value="saved">Saved theme</option><option value="light">Light</option><option value="dark">Dark</option>
    </select></label>
    <p class="pv-inspector__note" data-pv-note>Your current flow. Submissions and bookings are simulated; external links are disabled.</p>
    <div class="pv-link-notice" data-pv-link role="status" hidden><span></span><button type="button">Dismiss</button></div>
  </div>`;
}

function wireInspectionControls(root, refresh, options = {}) {
  const controls = root.querySelector('.pv-inspector');
  const state = controls.querySelector('[data-pv-state]');
  const theme = controls.querySelector('[data-pv-theme]');
  const route = controls.querySelector('[data-pv-route]');
  const variant = controls.querySelector('[data-pv-variant]');
  const note = controls.querySelector('[data-pv-note]');
  if (options.followRoute) {
    const follow = document.createElement('option'); follow.value = 'follow'; follow.textContent = 'Follow the editor';
    route.prepend(follow); route.value = 'follow';
  }
  for (const [key, control] of Object.entries({ state, theme, route, variant })) {
    const initial = options.initialInspection?.[key];
    if ([...control.options].some(option => option.value === initial)) control.value = initial;
  }
  if (options.studio) { state.value = 'flow'; state.closest('label').hidden = true; }
  controls.addEventListener('change', event => {
    if (event.target === route && options.onFlowNavigate && route.value !== 'follow') {
      options.onFlowNavigate({ route: route.value });
      if (options.followRoute) route.value = 'follow';
    }
    refresh();
  });
  return (pkg) => {
    if (options.studio) state.value = 'flow';
    route.disabled = state.value !== 'flow';
    variant.disabled = !pkg.mag_ab?.b;
    if (variant.disabled) variant.value = 'A';
    const selectedRoute = route.value === 'follow' ? options.followRoute() : route.value;
    note.textContent = state.value === 'flow'
      ? options.progressive ? `${{ entry: 'Starts as configured', chat: 'Main conversation', book: 'Appointments path', support: 'Support path' }[selectedRoute]}. This preview won’t create leads or bookings.`
        : 'Your current flow. Submissions and bookings are simulated; external links are disabled.'
      : 'Sample state with your appearance and example answers. Your saved flow stays the same.';
    if (options.progressive && (variant.value !== 'A' || theme.value !== 'saved' || state.value !== 'flow')) {
      note.textContent += ` Preview options: ${state.selectedOptions[0].textContent}, variant ${variant.value}, ${theme.selectedOptions[0].textContent.toLowerCase()}.`;
    }
    const vars = pkg.mag_theme_vars || {};
    if (theme.value !== 'saved' && !matchMagnetAppearancePreset(pkg.mag_font, vars)
      && MAGNET_THEME_COLOR_FIELDS.some(({ key }) => Object.prototype.hasOwnProperty.call(vars, key))) {
      note.textContent += ' Your custom colors stay in place in this theme preview.';
    }
    return { state: state.value, theme: theme.value, route: selectedRoute, variant: variant.value };
  };
}

function wirePreviewLinks(root, frame) {
  const notice = root.querySelector('[data-pv-link]');
  const hide = () => { notice.hidden = true; };
  const receive = (event) => {
    if (event.source !== frame.contentWindow || event.data?.type !== 'm5m-preview-link' || typeof event.data.url !== 'string') return;
    let url;
    try { url = new URL(event.data.url); } catch { return; }
    if (!['https:', 'http:', 'tel:', 'mailto:'].includes(url.protocol)) return;
    notice.querySelector('span').textContent = `${event.data.booking ? 'Booking destination' : 'Link destination'}: ${url.href.slice(0, 2000)}. Opens on your published site.`;
    notice.hidden = false;
  };
  notice.querySelector('button').addEventListener('click', hide);
  frame.addEventListener('load', hide);
  window.addEventListener('message', receive);
  return () => { window.removeEventListener('message', receive); frame.removeEventListener('load', hide); };
}

function discloseInspectionControls(root) {
  const inspector = root.querySelector('.pv-inspector');
  const disclosure = document.createElement('details'); disclosure.className = 'pv-options';
  const summary = document.createElement('summary'); summary.textContent = 'Preview options'; disclosure.append(summary);
  inspector.before(disclosure); disclosure.append(inspector);
  // Keep simulation notices, runtime errors and blocked destinations visible.
  const status = root.querySelector('[data-pv-runtime-status]');
  status.classList.add('pv-runtime-note');
  disclosure.after(status, root.querySelector('[data-pv-retry]'), root.querySelector('[data-pv-note]'), root.querySelector('[data-pv-link]'));
}

/**
 * Mount a persistent live-preview pane into `container` (the edit modal's right column
 * on wide screens). `getPkg` returns the current mag_* package; call the returned
 * refresh() (debounced by the caller) whenever the form changes. Shows an "Updating…"
 * badge while the iframe re-renders. Reuses buildPreviewDocument() with the modal preview.
 */
export function mountPreviewPane(container, getPkg, options = {}) {
  ensureStyles();
  container.innerHTML = `
    <div class="pv-pane${options.studio ? ' pv-pane--studio' : ''}">
      <div class="pv-pane__hd">${options.studio ? '<span class="pv-canvas-title">Preview</span>' : '<span class="material-symbols-outlined" aria-hidden="true">visibility</span>Live preview'}
        ${options.studio && options.getEditingStep ? '<span class="pv-question-tools"><span class="pv-seg" role="group" aria-label="Canvas mode"><button type="button" data-pv-mode="edit" aria-label="Edit preview">Edit</button><button type="button" data-pv-mode="try" aria-label="Test conversation">Test</button></span></span>' : ''}
        <span class="pv-pane__hdright">
          <span class="pv-pane__badge" hidden><span class="pv-pane__dot"></span>Updating…</span>
          <span class="pv-seg pv-pane__seg" role="group" aria-label="Preview device">
            <button type="button" data-device="mobile" class="${options.studio ? '' : 'is-on'}" title="Mobile" aria-label="Mobile preview"><span class="material-symbols-outlined" aria-hidden="true">smartphone</span></button>
            <button type="button" data-device="tablet" title="Tablet" aria-label="Tablet preview"><span class="material-symbols-outlined" aria-hidden="true">tablet_mac</span></button>
            <button type="button" data-device="desktop" class="${options.studio ? 'is-on' : ''}" title="${options.studio ? 'Desktop preview' : 'Full Screen Preview — Desktop'}" aria-label="${options.studio ? 'Desktop preview' : 'Full Screen Preview — Desktop'}"${options.studio ? '' : ' aria-haspopup="dialog"'}><span class="material-symbols-outlined" aria-hidden="true">desktop_windows</span></button>
          </span>
          ${options.progressive ? '<button type="button" class="pv-iconbtn" data-pv-restart aria-label="Restart preview" title="Restart preview"><span class="material-symbols-outlined" aria-hidden="true">refresh</span></button>' : ''}
        </span>
      </div>
      ${inspectionControlsHTML()}
      ${options.getFlowNavigation ? '<details class="pv-flow-navigation" hidden><summary aria-label="Choose a question"><span data-pv-location></span></summary><div class="pv-question-picker"><label data-pv-path-label>Visitor path<select data-pv-flow-route></select></label><label>Find a question<input type="search" data-pv-find-question placeholder="Search question wording…"></label><label>Question<select data-pv-flow-question size="6"></select></label><p data-pv-no-questions hidden>No matching questions. Try different wording.</p></div></details>' : ''}
      ${options.studio ? '<p class="pv-studio-status" data-pv-studio-status role="status"></p>' : ''}
      ${options.studio ? '<p class="pv-canvas-context"><span data-pv-context-help></span></p>' : ''}
      <div class="pv-pane__body"><div class="pv-pane__vp"><iframe class="pv-pane__frame" data-device="${options.studio ? 'desktop' : 'mobile'}" sandbox="allow-scripts allow-forms" title="Live magnet preview"></iframe></div></div>
      ${options.studio && options.answerAuthoring ? '<details class="pv-submission-result" data-pv-submission-result hidden></details>' : ''}
      ${infoHintHTML('Live render of the real widget. Capture is disabled, so no test leads.')}
    </div>`;
  wireInfoHint(container);
  const frame = container.querySelector('.pv-pane__frame');
  const badge = container.querySelector('.pv-pane__badge');
  const detachLinks = wirePreviewLinks(container, frame);
  const studioStatus = container.querySelector('[data-pv-studio-status]');
  const resultPanel = container.querySelector('[data-pv-submission-result]');
  let resultDefinition = null, pendingSubmissionResult = null;
  const clearSubmissionResult = () => {
    pendingSubmissionResult = null;
    if (resultPanel) renderPreviewSubmissionResult(resultPanel, null);
  };
  const questionTools = container.querySelector('.pv-question-tools');
  const canvasContext = container.querySelector('.pv-canvas-context');
  const showContext = (title = '', help = '') => {
    if (!canvasContext) return;
    canvasContext.querySelector('[data-pv-context-help]').textContent = help || 'Test the conversation. No leads or bookings are created.';
    canvasContext.title = title;
  };
  const flowNavigation = container.querySelector('.pv-flow-navigation');
  const dismissPopovers = event => {
    for (const popover of container.querySelectorAll('.pv-flow-navigation[open],.pv-options[open]')) if (!popover.contains(event.target)) popover.open = false;
  };
  if (options.studio) document.addEventListener('pointerdown', dismissPopovers);
  const filterQuestions = () => {
    const query = flowNavigation.querySelector('[data-pv-find-question]').value.trim().toLowerCase();
    const navigation = options.getFlowNavigation();
    if (!navigation) return;
    const matches = navigation.questions.filter(item => item.label.toLowerCase().includes(query));
    const question = flowNavigation.querySelector('[data-pv-flow-question]');
    question.innerHTML = '<option value="">Choose a question to edit…</option>' + matches.map(item => `<option value="${esc(item.uid)}">${esc(item.label)}</option>`).join('');
    question.size = Math.max(2, Math.min(7, matches.length + 1));
    question.value = matches.some(item => item.uid === navigation.uid) ? navigation.uid : '';
    flowNavigation.querySelector('[data-pv-no-questions]').hidden = !!matches.length;
  };
  const syncFlowNavigation = () => {
    if (!flowNavigation) return;
    const navigation = options.getFlowNavigation();
    flowNavigation.hidden = !navigation;
    container.querySelector('.pv-canvas-title').hidden = !!navigation;
    if (!navigation) return;
    const route = flowNavigation.querySelector('[data-pv-flow-route]');
    route.innerHTML = navigation.routes.map(item => `<option value="${esc(item.value)}">${esc(item.label)}</option>`).join('');
    route.value = navigation.route;
    flowNavigation.querySelector('[data-pv-path-label]').hidden = navigation.routes.length < 2;
    const index = navigation.questions.findIndex(item => item.uid === navigation.uid);
    flowNavigation.querySelector('[data-pv-location]').textContent = `${navigation.routes.find(item => item.value === navigation.route)?.label || 'Conversation'} › ${index < 0 ? 'Questions' : `Question ${index + 1} of ${navigation.questions.length}`}`;
    filterQuestions();
    container.querySelector('.pv-canvas-title').hidden = true;
  };
  flowNavigation?.addEventListener('input', event => { event.stopPropagation(); if (event.target.matches('[data-pv-find-question]')) filterQuestions(); });
  flowNavigation?.addEventListener('keydown', event => {
    if (event.key === 'Escape' && flowNavigation.open) { event.preventDefault(); event.stopPropagation(); flowNavigation.open = false; flowNavigation.querySelector('summary').focus(); }
  });
  flowNavigation?.addEventListener('change', event => {
    event.stopPropagation();
    if (!event.target.matches('[data-pv-flow-route],[data-pv-flow-question]')) return;
    const route = flowNavigation.querySelector('[data-pv-flow-route]').value;
    const uid = event.target.matches('[data-pv-flow-question]') ? event.target.value : null;
    if (uid === '') return;
    flowNavigation.querySelector('[data-pv-find-question]').value = '';
    options.onFlowNavigate?.({ route, uid });
    refresh();
    if (event.target.matches('[data-pv-flow-question]')) { flowNavigation.open = false; flowNavigation.querySelector('summary').focus({ preventScroll: true }); }
  });
  let editingSelection = null, editingKey = '', editMode = false, finishEditing = false;
  const syncModes = () => {
    const activeEdit = editMode || finishEditing;
    questionTools?.querySelectorAll('[data-pv-mode]').forEach(button => {
      const active = (button.dataset.pvMode === 'edit') === activeEdit;
      button.classList.toggle('is-on', active); button.setAttribute('aria-pressed', String(active));
      if (button.dataset.pvMode === 'edit') { button.disabled = !editingSelection && !options.getFinishState?.(); button.title = button.disabled ? 'Choose a question to edit' : 'Edit preview'; }
    });
    const restart = container.querySelector('[data-pv-restart]');
    if (restart && options.studio) restart.hidden = activeEdit;
  };
  const changeEditMode = enabled => {
    const next = !!enabled && !!editingSelection;
    if (next === editMode) return;
    editMode = next;
    if (studioStatus) studioStatus.textContent = '';
    options.onEditModeChange?.(editMode);
  };
  const syncEditingSelection = (followingRuntime = false) => {
    syncFlowNavigation();
    if (!questionTools) return;
    editingSelection = options.getEditingStep() || null;
    const key = editingSelection ? `${editingSelection.route || 'chat'}:${editingSelection.uid ?? editingSelection.step?.id ?? editingSelection.index}` : '';
    if (key !== editingKey) { editingKey = key; if (!followingRuntime) changeEditMode(!!editingSelection); }
    if (!editingSelection) changeEditMode(false);
    syncModes();
    const finish = options.getFinishState?.();
    container.querySelector('.pv-canvas-title').textContent = finish ? finish === 'review' ? 'Review answers' : 'Confirmation' : 'Preview';
    container.querySelector('[data-pv-route]').closest('label').hidden = editMode;
    container.querySelector('.pv-pane').classList.toggle('is-editing-question', editMode);
    frame.title = editMode ? 'Selected question — read-only editing preview' : 'Live magnet preview';
    if (editMode) showContext('Editing preview', 'Editing preview · Click the question to edit wording. Switch to Test to answer.');
    else showContext();
  };
  let bridgeToken = '', bridgeSnapshot = null, pendingSnapshot = null, lastStructure = null, lastSdk = null, restoring = false, studioLoading = false, destroyed = false;
  let pendingRuntimePosition = null, followOnReady = false;
  const followRuntimePosition = position => {
    options.onRuntimeNavigate(position);
    syncEditingSelection(true);
    const routeName = { chat: 'Main conversation', book: 'Appointments', support: 'Support', call: 'Phone', email: 'Email' }[position.route];
    const location = position.stage ? position.stage === 'review' ? 'Review answers' : 'Confirmation' : `${routeName}${position.index === null ? '' : ` · question ${position.index + 1}`}`;
    container.querySelector('[data-pv-note]').textContent = `${location}. The editor follows this preview. Test answers are not saved.`;
  };
  const detachBridge = options.studio ? listenToPreviewBridge(frame, () => bridgeToken, message => {
    if (destroyed) return;
    if (resultPanel && !editMode && !finishEditing && message.kind === 'capture-result') {
      pendingSubmissionResult = resultDefinition ? previewSubmissionResult(resultDefinition, message.capture) : null;
      return;
    }
    if (resultPanel && message.kind === 'position') {
      if (message.position?.stage === 'success' && pendingSubmissionResult) renderPreviewSubmissionResult(resultPanel, pendingSubmissionResult);
      else if (!message.position?.stage) clearSubmissionResult();
    }
    if (editMode && message.kind === 'edit-wording') { options.onEditWording?.(); return; }
    if (!editMode && message.kind === 'position' && options.onRuntimeNavigate) {
      const position = message.position;
      const contact = ['call', 'email'].includes(position?.route) && position.index === null;
      const finish = ['review', 'success'].includes(position?.stage);
      if (!position || (!finish && !contact && (!['chat', 'book', 'support'].includes(position.route) || !Number.isInteger(position.index) || position.index < 0))) return;
      pendingRuntimePosition = position;
      if (position.interactive && !restoring) followRuntimePosition(position);
      return;
    }
    if (!editMode && message.kind === 'snapshot' && message.snapshot && Array.isArray(message.snapshot.actions) && message.snapshot.actions.length <= 60) {
      if (studioLoading) pendingSnapshot = message.snapshot;
      else bridgeSnapshot = message.snapshot;
    } else if (message.kind === 'ready') {
      if (pendingSnapshot) bridgeSnapshot = pendingSnapshot;
      pendingSnapshot = null;
      if (editMode && studioStatus) studioStatus.textContent = '';
      studioLoading = false; restoring = false; hideBadge();
      if (followOnReady && pendingRuntimePosition && !editMode) followRuntimePosition(pendingRuntimePosition);
      followOnReady = false;
    } else if (message.kind === 'failed') {
      studioLoading = false;
      if (editMode || options.getFinishState?.()) {
        studioStatus.textContent = 'This screen could not be shown on its own. Test the conversation to inspect it.';
        hideBadge(); return;
      }
      bridgeSnapshot = null;
      pendingSnapshot = null;
      if (restoring) {
        if (editingSelection) changeEditMode(true);
        else followOnReady = true;
        studioStatus.textContent = editingSelection ? 'Test position could not be restored. Showing the question you are editing.' : 'Preview restarted because your test position could not be restored.';
        refresh();
      } else { studioStatus.textContent = 'The preview could not start. Check the conversation, then restart the preview.'; hideBadge(); }
    }
  }) : () => {};

  let expandedPreview = null;
  let hideTimer = null;
  let shownAt = 0;
  const MIN_VISIBLE = 450; // keep the badge up long enough to actually read (and not flicker)
  const hideBadge = () => {
    if (studioLoading) return;
    const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - shownAt;
    clearTimeout(hideTimer);
    if (elapsed < MIN_VISIBLE) hideTimer = setTimeout(() => { badge.hidden = true; }, MIN_VISIBLE - elapsed);
    else badge.hidden = true;
  };
  frame.addEventListener('load', hideBadge);
  const refresh = () => {
    if (destroyed) return;
    pendingRuntimePosition = null;
    let doc;
    try {
      finishEditing = false;
      syncEditingSelection();
      const view = experience.get(getPkg()); if (!view) return;
      const inspect = inspection(view.pkg);
      let bridge = null;
      let questionScene = null;
      let finishScene = null;
      const finishState = options.getFinishState?.();
      // If a test cannot be replayed, keep the author's current field and show
      // that question explicitly. Never leave Q2's editor beside a restarted Q1.
      if (options.studio && !editMode && !finishState && editingSelection && !followOnReady && bridgeSnapshot) {
        const compatible = previewStructure(view.pkg, { ...inspect, state: 'flow' }) === lastStructure
          && (view.sdk || SDK_RAW) === lastSdk && bridgeSnapshot.supported && /^(input:|choices:)/.test(bridgeSnapshot.location || '');
        const hadProgress = bridgeSnapshot.actions.length || bridgeSnapshot.draft?.value || !bridgeSnapshot.supported;
        if (hadProgress && !compatible) {
          changeEditMode(true); syncEditingSelection(); bridgeSnapshot = null;
        }
      }
      if (editMode) {
        bridgeToken = crypto.randomUUID(); pendingSnapshot = null; restoring = false; studioLoading = true;
        questionScene = { selection: editingSelection, token: bridgeToken };
        const route = { chat: 'Main conversation', book: 'Appointments', support: 'Support' }[editingSelection.route] || editingSelection.route || 'Main conversation';
        const note = `Editing question ${editingSelection.index + 1} of ${editingSelection.total} · ${route} · isolated preview. Switch to Test to try paths.`;
        container.querySelector('[data-pv-note]').textContent = note;
      } else if (options.studio && ['review', 'success'].includes(finishState)) {
        bridgeToken = crypto.randomUUID(); pendingSnapshot = null; restoring = false; studioLoading = true;
        bridgeSnapshot = null;
        finishScene = { token: bridgeToken, state: finishState };
        finishEditing = true; syncModes();
        showContext(`${finishState === 'review' ? 'Review answers' : 'Confirmation'} · Sample answers`,
          finishState === 'review' && !view.pkg.mag_summary ? 'Sample answers · Enable answer review on the right to include this screen.' : 'Editing preview · Sample answers. Switch to Test to try the full conversation.');
        container.querySelector('[data-pv-note]').textContent = 'Your finish screen with sample answers. No leads or bookings are created.';
        frame.title = 'Finish screen — sample answers, read-only editing preview';
      } else if (options.studio) {
        inspect.state = 'flow';
        const structure = previewStructure(view.pkg, inspect);
        const sdk = view.sdk || SDK_RAW;
        const hadProgress = bridgeSnapshot && (bridgeSnapshot.actions.length || bridgeSnapshot.draft?.value || !bridgeSnapshot.supported);
        const compatible = structure === lastStructure && sdk === lastSdk && bridgeSnapshot?.supported
          && /^(input:|choices:)/.test(bridgeSnapshot.location || '');
        const restore = compatible ? bridgeSnapshot : null;
        if (hadProgress && !restore) studioStatus.textContent = 'Preview restarted because the conversation or test interaction changed.';
        if (!restore) bridgeSnapshot = null;
        bridgeToken = crypto.randomUUID();
        pendingSnapshot = null;
        restoring = !!restore;
        studioLoading = true;
        bridge = { token: bridgeToken, restore, captureResults: !!resultPanel };
        lastStructure = structure; lastSdk = sdk;
      }
      doc = buildPreviewDocument(view.pkg, { ...inspect, studio: !!options.studio, bridge, questionScene, finishScene, preserveEditorFocus: !!options.preserveEditorPosition, ...(view.sdk ? { sdk: view.sdk } : {}) });
      resultDefinition = JSON.parse(JSON.stringify(inspect.variant === 'B' && view.pkg.mag_ab?.b ? { ...view.pkg, ...view.pkg.mag_ab.b } : view.pkg));
    } catch {
      if (studioStatus) studioStatus.textContent = 'Complete this setting to update the preview. The previous preview is still shown.';
      return;
    }
    shownAt = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    clearSubmissionResult();
    badge.hidden = false;
    frame.srcdoc = doc;
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      badge.hidden = true;
      if (studioLoading && studioStatus) studioStatus.textContent = 'The preview is taking longer to update. Restart the preview if it does not respond.';
    }, options.studio ? 10000 : 1500); // safety net if a runtime cannot start
  };
  const inspection = wireInspectionControls(container, refresh, options);
  const experience = mountExperienceControls(container, options, refresh);
  const testConversation = () => {
    options.onTestConversation?.(); syncEditingSelection(true);
    followOnReady = true; changeEditMode(false); refresh();
  };
  questionTools?.addEventListener('click', event => {
    if (destroyed) return;
    const mode = event.target.closest('[data-pv-mode]');
    if (mode?.dataset.pvMode === 'try') testConversation();
    else if (mode) { followOnReady = false; changeEditMode(true); refresh(); }
  });
  if (options.progressive) {
    discloseInspectionControls(container);
    if (options.studio) {
      const disclosure = container.querySelector('.pv-options');
      disclosure.querySelector('summary').innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">settings</span>';
      disclosure.querySelector('summary').setAttribute('aria-label', 'Preview options');
      const settings = document.createElement('div'); settings.className = 'pv-studio-options-body';
      const device = container.querySelector('.pv-pane__seg');
      for (const button of device.querySelectorAll('[data-device]')) button.append(document.createTextNode(` ${{ mobile: 'Mobile', tablet: 'Tablet', desktop: 'Desktop' }[button.dataset.device]}`));
      settings.append(device, container.querySelector('.pv-inspector'), container.querySelector('[data-pv-note]'), container.querySelector('.pv-hintwrap'));
      disclosure.append(settings);
      // Only one popover at a time, dismissed with Escape or an outside click.
      const popovers = [disclosure, flowNavigation].filter(Boolean);
      for (const popover of popovers) popover.addEventListener('toggle', () => {
        if (popover.open) for (const other of popovers) if (other !== popover) other.open = false;
      });
      container.querySelector('.pv-pane__hd').append(disclosure);
      if (flowNavigation) container.querySelector('.pv-pane__hd').prepend(flowNavigation);
      disclosure.addEventListener('keydown', event => {
        if (event.key === 'Escape' && disclosure.open) {
          event.preventDefault(); disclosure.open = false; disclosure.querySelector('summary').focus();
        }
      });
    }
    container.querySelector('[data-pv-restart]').addEventListener('click', () => {
      bridgeSnapshot = null;
      if (studioStatus) studioStatus.textContent = 'Preview restarted. Test answers have been cleared.';
      if (options.studio) testConversation(); else refresh();
    });
  }
  // Keep the actual selected viewport width, even in a narrow side pane. Bring the
  // widget into view instead of silently rendering "Desktop" at a mobile breakpoint.
  const positionViewport = () => {
    let mode;
    try { mode = getPkg().mag_display_mode; } catch { return; }
    const viewport = frame.parentElement;
    const overflow = viewport.scrollWidth - viewport.clientWidth;
    viewport.scrollLeft = mode === 'inline' ? 0 : mode === 'modal' ? overflow / 2 : overflow;
  };
  frame.addEventListener('load', positionViewport);
  let deviceChosen = false;
  const setDevice = device => {
    frame.dataset.device = device;
    container.querySelectorAll('.pv-pane__seg button').forEach(button => { button.classList.toggle('is-on', button.dataset.device === device); button.setAttribute('aria-pressed', String(button.dataset.device === device)); });
    positionViewport();
  };
  const autoDevice = () => {
    if (!options.studio || deviceChosen || destroyed) return;
    const width = frame.parentElement.clientWidth || container.clientWidth;
    if (width > 0) setDevice(width < 640 ? 'mobile' : 'desktop');
  };
  const resizeObserver = options.studio && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(autoDevice) : null;
  resizeObserver?.observe(frame.parentElement);
  autoDevice();

  // Desktop opens outside the narrow pane so its content is not clipped.
  container.querySelector('.pv-pane__seg').addEventListener('click', (e) => {
    if (destroyed) return;
    const btn = e.target.closest('button[data-device]');
    if (!btn) return;
    deviceChosen = true;
    if (btn.dataset.device === 'desktop' && !options.studio) {
      const initialInspection = inspection(getPkg());
      if (options.onExpand) options.onExpand(initialInspection);
      else {
        expandedPreview?.close();
        expandedPreview = openPreviewModal(getPkg, { ...options, fullScreen: true, initialInspection });
      }
      return;
    }
    if (btn.dataset.device === frame.dataset.device) return;
    setDevice(btn.dataset.device);
  });

  refresh();
  return { refresh, editSelectedStep() {
    if (destroyed) return;
    syncEditingSelection(); changeEditMode(true); refresh();
  }, destroy() {
    destroyed = true; expandedPreview?.close(); experience.destroy(); clearTimeout(hideTimer); detachLinks(); detachBridge(); resizeObserver?.disconnect();
    document.removeEventListener('pointerdown', dismissPopovers);
    frame.removeEventListener('load', hideBadge); frame.removeEventListener('load', positionViewport);
    if (options.studio) frame.srcdoc = ''; // Release the sandbox's observer, timers and test answers.
  } };
}

/**
 * Open the preview overlay. `getPkg` is a function returning the mag_* package to render —
 * a function (not a value) so Refresh + device toggle re-snapshot the latest working state.
 */
export function openPreviewModal(getPkg, options = {}) {
  ensureStyles();
  let device = 'desktop';

  const scrim = document.createElement('div');
  scrim.className = options.fullScreen ? 'pv-scrim pv-scrim--full' : 'pv-scrim';
  scrim.innerHTML = `
    <div class="pv-modal" role="dialog" aria-modal="true" aria-label="${options.manageExperience ? 'Magnet experience comparison' : 'Magnet preview'}">
      <div class="pv-head">
        <span class="pv-title"><span class="material-symbols-outlined" aria-hidden="true">visibility</span> ${options.manageExperience ? 'Experience comparison' : options.fullScreen ? 'Full Screen Preview' : 'Live preview'}</span>
        <div class="pv-ctrls">
          <div class="pv-seg" role="group" aria-label="Device">
            <button type="button" data-device="desktop" class="is-on"><span class="material-symbols-outlined" aria-hidden="true">desktop_windows</span>Desktop</button>
            <button type="button" data-device="tablet"><span class="material-symbols-outlined" aria-hidden="true">tablet_mac</span>Tablet</button>
            <button type="button" data-device="mobile"><span class="material-symbols-outlined" aria-hidden="true">smartphone</span>Mobile</button>
          </div>
          <button class="pv-iconbtn" type="button" data-x="refresh" title="Restart preview"><span class="material-symbols-outlined" aria-hidden="true">refresh</span></button>
          <button class="pv-iconbtn" type="button" data-x="close" title="Close"><span class="material-symbols-outlined" aria-hidden="true">close</span></button>
        </div>
      </div>
      ${inspectionControlsHTML()}
      <div class="pv-body">
        <div>
          <div class="pv-stage" data-device="desktop"><iframe class="pv-frame" sandbox="allow-scripts allow-forms" title="Magnet preview"></iframe></div>
          ${infoHintHTML('Live render of the real widget. Preview changes are not saved.')}
        </div>
      </div>
    </div>`;
  document.body.appendChild(scrim);
  requestAnimationFrame(() => scrim.classList.add('open'));
  wireInfoHint(scrim);

  const frame = scrim.querySelector('.pv-frame');
  const stage = scrim.querySelector('.pv-stage');
  const detachLinks = wirePreviewLinks(scrim, frame);
  const rebuild = () => { try { const view = experience.get(getPkg()); if (!view) return;
    frame.srcdoc = buildPreviewDocument(view.pkg, { ...inspection(view.pkg), ...(view.sdk ? { sdk: view.sdk } : {}) });
  } catch { /* getPkg may throw mid-edit */ } };
  const inspection = wireInspectionControls(scrim, rebuild, options);
  const experience = mountExperienceControls(scrim, options, rebuild);
  if (options.fullScreen) discloseInspectionControls(scrim);
  rebuild();

  const previousFocus = document.activeElement;
  const onKey = event => {
    if (event.key === 'Escape') { event.stopImmediatePropagation(); close(); }
    if (event.key === 'Tab') {
      const focusable = [...scrim.querySelectorAll('button, select, input, summary, iframe')]
        .filter(el => !el.disabled && !el.closest('[hidden]'));
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  };
  let closed = false;
  const close = () => { if (closed) return; closed = true; experience.destroy(); detachLinks(); document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('hashchange', close); scrim.classList.remove('open'); setTimeout(() => scrim.remove(), 180); previousFocus?.focus(); };
  document.addEventListener('keydown', onKey, true);
  window.addEventListener('hashchange', close);
  scrim.querySelector('[data-x="close"]').focus();
  scrim.querySelector('[data-x="close"]').addEventListener('click', close);
  scrim.querySelector('[data-x="refresh"]').addEventListener('click', rebuild);
  scrim.addEventListener('click', (e) => { if (e.target === scrim) close(); });

  scrim.querySelector('.pv-seg').addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-device]');
    if (!btn || btn.dataset.device === device) return;
    device = btn.dataset.device;
    scrim.querySelectorAll('.pv-seg button').forEach((b) => b.classList.toggle('is-on', b.dataset.device === device));
    stage.dataset.device = device;
    rebuild(); // fresh viewport
  });

  return { close, rebuild };
}

export default { openPreviewModal, mountPreviewPane, magnetToPackage };
